import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import useAsyncEffect from 'use-async-effect';

import { ITrainerToolsContext } from './interfaces';
import { Screens } from '../interfaces';
import {
  IEventAsset,
  EventAssetType,
} from '@providers/EventProvider/interfaces';
import EventAssetsService from '@services/EventAssetsService';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { useMytaverse } from '@providers/MytaverseProvider';
import { DEFAULT_HEIGHT, DEFAULT_SIZE } from '@features/TrainerTools/constants';

export const TrainerToolsContext = createContext<ITrainerToolsContext>(
  {} as ITrainerToolsContext,
);

export const useTrainerToolsContent = () => useContext(TrainerToolsContext);

type MytaverseProviderProps = {
  children: ReactNode;
};

export const TrainerToolsProvider: React.FC<MytaverseProviderProps> = ({
  children,
}) => {
  const [activeScreen, setActiveScreen] = useState<Screens>(Screens.INIT);
  const [tags, setTags] = useState<string[]>([]);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [objects, setObjects] = useState<IEventAsset[]>([]);

  const [temporaryObject, setTemporaryObject] = useState<IEventAsset | null>(
    null,
  );
  const [activeObject, setActiveObject] = useState<IEventAsset | null>(null);
  const [activeObjectReady, setActiveObjectReady] = useState(false);

  const [sizeLimits, setSizeLimits] = useState({ min: 0.1, max: 10 });
  const [heightLimits, setHeightLimits] = useState({ min: -100, max: 200 });
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [size, setSize] = useState(DEFAULT_SIZE);

  const [isExploration, setIsExploration] = useState(false);

  const { currentEventId } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const changeFilterTags = useCallback((tag: string) => {
    setActiveTags((prevState) => {
      return prevState.includes(tag)
        ? prevState.filter((item) => item !== tag)
        : [...prevState, tag];
    });
  }, []);

  const clearFilters = useCallback(() => {
    setActiveTags([]);
  }, []);

  const handleExploration = useCallback(() => {
    setIsExploration((prevState) => {
      sendMessageToUnreal({
        action: WebsocketAction.Asset3DExploration,
        payload: {
          isActive: !prevState,
        },
      });

      return !prevState;
    });
  }, []);

  useAsyncEffect(async () => {
    try {
      const { assets } = await EventAssetsService.getAssets({
        eventId: currentEventId,
        type: EventAssetType.RESOURCE,
      });

      if (assets.length > 0) {
        assets.sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        );

        setObjects(assets);
        setTags([...new Set(assets.flatMap((asset) => asset.tags || []))]);
      } else {
        setObjects([]);
      }
    } catch (e) {
      MytaverseLogger.log(`Event assets: ${getCatchErrorMessage(e)}`);
    }
  }, [currentEventId]);

  return (
    <TrainerToolsContext.Provider
      value={{
        activeScreen,
        setActiveScreen,
        tags,
        activeTags,
        changeFilterTags,
        clearFilters,
        objects,
        temporaryObject,
        setTemporaryObject,
        activeObject,
        setActiveObject,
        sizeLimits,
        setSizeLimits,
        heightLimits,
        setHeightLimits,
        activeObjectReady,
        setActiveObjectReady,
        isExploration,
        setIsExploration,
        handleExploration,
        height,
        setHeight,
        size,
        setSize,
      }}
    >
      {children}
    </TrainerToolsContext.Provider>
  );
};
